/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import jwtDecode from 'jwt-decode';
import {
    CAPIError,
    CommerceApiConfig,
    CommerceApiCResponse,
    OCAPIError,
    RequiredParameters,
} from '~/lib/data-contract/salesforce';
import { GiftCardError, HTTPError, MultiError } from './http-error';

/**
 * Compares the token age against the issued and expiry times. If the token's age is
 * within 60 seconds of its valid time (or exceeds it), we consider the token invalid.
 * @function
 * @param {string} token - The JWT bearer token to be inspected
 * @returns {boolean}
 */
export function isTokenValid(token: string | null | undefined) {
    if (!token) {
        return false;
    }
    const { exp, iat } = jwtDecode<{ exp: number; iat: number }>(token.replace('Bearer ', ''));
    const validTimeSeconds = exp - iat - 60;
    const tokenAgeSeconds = Date.now() / 1000 - iat;
    if (validTimeSeconds > tokenAgeSeconds) {
        return true;
    }

    return false;
}

// Returns formulated body for SopperLogin getToken endpoint
export function createGetTokenBody(
    urlString: string,
    slasCallbackEndpoint: string,
    codeVerifier: string,
) {
    const url = new URL(urlString);
    const urlParams = new URLSearchParams(url.search);
    const usid = urlParams.get('usid')!;
    const code = urlParams.get('code')!;
    return {
        grantType: 'authorization_code_pkce',
        code,
        usid,
        codeVerifier: codeVerifier,
        redirectUri: slasCallbackEndpoint,
    };
}

export function createGetTokenBodyFromSoMeLogin(
    usid: string,
    code: string,
    slasCallbackEndpoint: string,
    codeVerifier: string,
) {
    return {
        grantType: 'authorization_code_pkce',
        code,
        usid,
        codeVerifier: codeVerifier,
        redirectUri: slasCallbackEndpoint,
    };
}

// Ocapi related utilities

const toCamel = (str: string): string => {
    if (str.startsWith('_') || str.startsWith('c_')) {
        return str;
    }
    return str.replace(/([-_][a-z])/gi, ($1) => {
        return $1.toUpperCase().replace('-', '').replace('_', '');
    });
};

const isObject = (obj: any) => {
    return obj === Object(obj) && !Array.isArray(obj) && typeof obj !== 'function';
};

export const keysToCamel = (obj: any): any => {
    if (isObject(obj)) {
        const n: any = {};

        Object.keys(obj).forEach((k) => {
            n[toCamel(k)] = keysToCamel(obj[k]);
        });

        return n;
    } else if (Array.isArray(obj)) {
        return obj.map((i) => {
            return keysToCamel(i);
        });
    }

    return obj;
};

export const camelCaseKeysToUnderscore = (_obj: any) => {
    if (typeof _obj != 'object') return _obj;

    // Copy the incoming object so we dont mutate it
    let obj;
    if (Array.isArray(_obj)) {
        obj = [..._obj];
    } else {
        obj = { ..._obj };
    }

    // eslint-disable-next-line no-var
    for (var oldName in obj) {
        // Camel to underscore

        const newName = oldName.replace(/([A-Z])/g, ($1) => {
            return '_' + $1.toLowerCase();
        });

        // Only process if names are different
        if (newName != oldName) {
            // Check for the old property name to avoid a ReferenceError in strict mode.
            if (Object.prototype.hasOwnProperty.call(obj, oldName)) {
                obj[newName] = obj[oldName];
                delete obj[oldName];
            }
        }

        // Recursion
        if (typeof obj[newName] == 'object') {
            obj[newName] = camelCaseKeysToUnderscore(obj[newName]);
        }
    }

    return obj;
};

const MULTI_ERROR = 'MULTI_ERROR';

export type CustomError = {
    errorMessage: string;
    errorCode: string;
} & {
    [key: string]: any;
};

// This function coverts errors/faults returned from the OCAPI API to the format that is returned from the CAPI
// I added the fault key to make life easier as it's hard to discern a CAPI error
export const convertOcapiFaultToCapiError = (error: OCAPIError): CAPIError => {
    if (error.arguments?.statusDetails?.errorData) {
        let innerError: CustomError | CustomError[] | CustomError[][] = JSON.parse(
            error.arguments.statusDetails.errorData,
        );
        if (Array.isArray(innerError)) {
            innerError = (
                Array.isArray(innerError[0]) ? innerError[0] : innerError
            ) as CustomError[]; // fix for SFCC returning an array inside an array;
            return {
                fault: true,
                title: innerError[0]?.errorMessage,
                type: innerError.length > 1 ? MULTI_ERROR : innerError[0].errorCode,
                detail: innerError[0]?.errorMessage,
                arguments: innerError.map((x) => ({
                    fault: true,
                    title: x.errorMessage,
                    type: x.errorCode,
                    detail: x.errorMessage,
                    arguments: x,
                })),
            };
        }
        return {
            fault: true,
            title: innerError.errorMessage,
            type: innerError.errorCode,
            detail: innerError.errorMessage,
            arguments: {
                fault: true,
                title: innerError.errorMessage,
                type: innerError.errorCode,
                detail: innerError.errorMessage,
                arguments: innerError,
            },
        };
    }

    if (error.arguments?.statusCode) {
        return {
            title: error.message,
            type: error.arguments.statusCode,
            detail: error.arguments.statusMessage,
            arguments: error.arguments,
            fault: true,
        };
    }

    return {
        title: error.message,
        type: error.type,
        detail: error.message,
        arguments: error.arguments,
        fault: true,
    };
};

// This function checks required parameters and or body for requests to OCAPI endpoints before sending
export const checkRequiredParameters = (
    listOfPassedParameters: RequiredParameters,
    listOfRequiredParameters: RequiredParameters,
) => {
    const isBodyOnlyRequiredParam =
        listOfRequiredParameters.includes('body') && listOfRequiredParameters.length === 1;

    if (!listOfPassedParameters.parameters && !isBodyOnlyRequiredParam) {
        return {
            title: `Parameters are required for this request`,
            type: `MissingParameters`,
            detail: `Parameters are required for this request`,
        };
    }

    if (listOfRequiredParameters.includes('body') && !listOfPassedParameters.body) {
        return {
            title: `Body is required for this request`,
            type: `MissingBody`,
            detail: `Body is  required for this request`,
        };
    }

    if (
        isBodyOnlyRequiredParam &&
        listOfRequiredParameters.includes('body') &&
        listOfPassedParameters.body
    ) {
        return undefined;
    }

    let undefinedValues = listOfRequiredParameters.filter(
        (req: string) => !Object.keys(listOfPassedParameters.parameters).includes(req),
    );

    undefinedValues = undefinedValues.filter((value: string) => value !== 'body');

    if (undefinedValues.length) {
        return {
            title: `The following parameters were missing from your resquest: ${undefinedValues.toString()}`,
            type: `MissingParameters`,
            detail: `The following parameters were missing from your resquest: ${undefinedValues.toString()}`,
        };
    } else {
        return undefined;
    }
};

// This function is used to interact with the OCAPI API
export const createOcapiFetch =
    (commerceAPIConfig: CommerceApiConfig) =>
    async (
        endpoint: string,
        method: string,
        args: any,
        _methodName: string,
        body?: any,
        customEndpoint?: boolean,
    ) => {
        const host = `${commerceAPIConfig.ocapiHost}`;

        const siteId = commerceAPIConfig.parameters.siteId;
        const headers = {
            ...args[0].headers,
            'Content-Type': 'application/json',
            'x-dw-client-id': commerceAPIConfig.parameters.clientId,
        };

        let response;
        const url = customEndpoint
            ? `${host}/${endpoint}`
            : `${host}/s/${siteId}/dw/shop/v21_3/${endpoint}`;

        response = await fetch(url, {
            method: method,
            headers: headers,
            ...(body && {
                body: JSON.stringify(body),
            }),
        });
        const httpStatus = response.status;

        if (httpStatus === 401) {
            window.location.reload();
            return false;
        }

        if (!args[1] && response.json) {
            response = await response.json();
        }

        const convertedResponse = keysToCamel(response);

        const { error, errorData } = convertedResponse;

        if (error && errorData) {
            // some endpoints return errorData, and in this case we should throw an HTTPError
            throw new HTTPError(
                convertedResponse.errorData.status,
                convertedResponse.errorData.errorCode,
                convertedResponse.errorData,
            );
        }

        if (error && !errorData) {
            // not all endpoints return errorData, and so in this case we return the response directly
            return convertedResponse;
        }

        if (convertedResponse.fault) {
            const error = convertOcapiFaultToCapiError(convertedResponse.fault);

            switch (error.type) {
                // TEMP(IMC-558): This is a temporary way of handling "CustomerBasketsQuotaExceededException" errors on attempts of creating baskets.
                case 'CustomerBasketsQuotaExceededException':
                case MULTI_ERROR: {
                    throw new MultiError(httpStatus, error.detail, error.arguments as CAPIError[]);
                }
                case 'GC_GENERAL_ERROR':
                case 'GC_NO_FUNDS_AVAILABLE':
                case 'GC_NOT_SUPPORTED':
                    throw new GiftCardError(httpStatus, error.type, error.arguments);
                default:
                    throw new HTTPError(httpStatus, error.detail, error.type);
            }
        }

        return convertedResponse;
    };

// This function derrives the SF Tenant Id from the SF OrgId
export const getTenantId = (orgId: string) => {
    // Derive tenant id id form org id
    const indexToStartOfTenantId = orgId.indexOf('_', orgId.indexOf('_') + 1);
    const tenantId = orgId.substring(indexToStartOfTenantId + 1);
    return tenantId;
};

/**
 * Indicates if an JSON response from the SDK should be considered an error
 * @param {object} jsonResponse - The response object returned from SDK calls
 * @returns {boolean}
 */
export const isError = (jsonResponse: CommerceApiCResponse | unknown | undefined) => {
    if (!jsonResponse) {
        return false;
    }

    const { detail, title, type } = jsonResponse as CommerceApiCResponse;
    if (detail && title && type) {
        return true;
    }

    return false;
};

/**
 * Decorator that wraps functions to handle error response.
 * @param {function} func - A function which returns a promise
 * @returns {function}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleAsyncError = <T>(func: (...args: any[]) => Promise<T>) => {
    return async (...args: unknown[]) => {
        const result = await func(...args);
        if (isError(result)) {
            throw new Error(JSON.stringify(result));
        }
        return result;
    };
};

/**
 * Converts snake-case strings to space separated or sentence case
 * strings by replacing '_' with a ' '.
 * @param {string} text snake-case text.
 * @returns {string} space separated string.
 */
export const convertSnakeCaseToSentenceCase = (text: string) => {
    return text.split('_').join(' ');
};

/**
 * No operation function. You can use this
 * empty function when you wish to pass
 * around a function that will do nothing.
 * Usually used as default for event handlers.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-function
export const noop = () => {};

export const NETS_CREDIT = 'NETS_CREDIT';
export const GIFT_CERTIFICATE = 'GIFT_CERTIFICATE';

export const encodeBase64 = (input: string) => {
    const utf8Encoder = new TextEncoder();
    const byteArray = utf8Encoder.encode(input);
    let binaryString = '';
    for (let i = 0; i < byteArray.byteLength; i++) {
        binaryString += String.fromCharCode(byteArray[i]);
    }
    return btoa(binaryString);
};
